/**
 * useXRayExportPoller
 *
 * Headless hook — meant to be called once from an always-mounted component
 * (XRayExportPollerInit) so it survives route navigation.
 *
 * Listens for the 'xrayExportQueued' CustomEvent dispatched by XrayExportPopup
 * after a successful submit, then polls the project's "Test Case Repository"
 * folder every 10 seconds until the enriched JSON file appears.
 * Fires a toast notification on completion.
 */

import { useEffect, useRef } from 'react';
import { useUserStore } from '../../../store/user/userStore';
import { ProjectFileService } from '../../../shared/middleware/services/ProjectFileService';
import { projectCachedAxios, invalidateCache } from '../../../shared/config/cachedAxiosConfig';
import { useToastNotificationContext } from '../../../shared/notifications/ToastNotificationContext';
import { useGenerationStore } from '../../../store/generation/generationStore';
import logger from '../../../shared/services/logging/logger';

const POLL_INTERVAL_MS = 10_000;
const MAX_POLLS = 60; // give up after 10 minutes

const useXRayExportPoller = () => {
  // Mutable map — interval callback reads it without needing a re-render cycle.
  // Map<jobId, { fileName, projectId, projectName, testSetName, count }>
  const pollsRef = useRef(new Map());

  // Refs to keep reactive values accessible inside the stable interval callback.
  const userDataRef  = useRef(null);
  const showSuccessRef = useRef(null);

  const userData    = useUserStore(state => state.userData);
  const { showSuccess } = useToastNotificationContext();

  useEffect(() => { userDataRef.current  = userData;    }, [userData]);
  useEffect(() => { showSuccessRef.current = showSuccess; }, [showSuccess]);

  // Listen for the CustomEvent dispatched by XrayExportPopup on successful submit.
  useEffect(() => {
    const handleQueued = (e) => {
      const { fileName, projectId, projectName, testSetName } = e.detail || {};
      if (!fileName || !projectId) return;

      const jobId = `${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
      pollsRef.current.set(jobId, { fileName, projectId, projectName, testSetName, count: 0 });
      useGenerationStore.getState().addXRayJob(jobId, testSetName, projectName);

      logger.info('useXRayExportPoller: poll registered', { jobId, fileName, projectId, projectName });
    };

    window.addEventListener('xrayExportQueued', handleQueued);
    return () => window.removeEventListener('xrayExportQueued', handleQueued);
  }, []);

  // Single stable polling interval — created once, lives for the component lifetime.
  useEffect(() => {
    const interval = setInterval(async () => {
      if (pollsRef.current.size === 0) return;

      const toRemove = [];

      for (const [jobId, poll] of pollsRef.current) {
        poll.count += 1;

        if (poll.count > MAX_POLLS) {
          toRemove.push(jobId);
          useGenerationStore.getState().removeXRayJob(jobId);
          logger.warn('useXRayExportPoller: poll timed out', { jobId, fileName: poll.fileName });
          continue;
        }

        try {
          // Bust the project file cache so we always get a fresh listing.
          invalidateCache(projectCachedAxios);

          const path = `${poll.projectName}/Test Case Repository`;
          const files = await ProjectFileService.getProjectFiles(
            poll.projectId,
            path,
            userDataRef.current
          );

          const found = Array.isArray(files) && files.some(
            f => f.name === poll.fileName || (f.key || f.path || '').endsWith(poll.fileName)
          );

          if (found) {
            toRemove.push(jobId);
            useGenerationStore.getState().removeXRayJob(jobId);
            showSuccessRef.current?.(
              'XRay Export Complete',
              `"${poll.testSetName}" test cases are ready in ${poll.projectName}`
            );
            logger.info('useXRayExportPoller: file detected — export complete', {
              jobId,
              fileName: poll.fileName,
            });
          }
        } catch (err) {
          logger.warn('useXRayExportPoller: poll check failed', { jobId, error: err.message });
        }
      }

      toRemove.forEach(id => pollsRef.current.delete(id));
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
};

export default useXRayExportPoller;
